import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { cn } from "../../lib/utils"

const DialogWidget = DialogPrimitive.Root

const DialogWidgetTrigger = DialogPrimitive.Trigger

const DialogWidgetPortal = DialogPrimitive.Portal

const DialogWidgetClose = DialogPrimitive.Close

const DialogWidgetOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "dsw-fixed dsw-inset-0 dsw-z-50 dsw-bg-black/80 data-[state=open]:dsw-animate-in data-[state=closed]:dsw-animate-out data-[state=closed]:dsw-fade-out-0 data-[state=open]:dsw-fade-in-0",
      className
    )}
    {...props}
  />
))
DialogWidgetOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogWidgetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogWidgetPortal>
    <DialogWidgetOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "dsw-fixed dsw-left-[50%] dsw-top-[50%] dsw-z-50 dsw-grid dsw-w-full dsw-max-w-lg dsw-translate-x-[-50%] dsw-translate-y-[-50%] dsw-gap-4 dsw-border dsw-bg-background dsw-p-6 dsw-shadow-lg dsw-duration-200 data-[state=open]:dsw-animate-in data-[state=closed]:dsw-animate-out data-[state=closed]:dsw-fade-out-0 data-[state=open]:dsw-fade-in-0 data-[state=closed]:dsw-zoom-out-95 data-[state=open]:dsw-zoom-in-95 data-[state=closed]:dsw-slide-out-to-left-1/2 data-[state=closed]:dsw-slide-out-to-top-[48%] data-[state=open]:dsw-slide-in-from-left-1/2 data-[state=open]:dsw-slide-in-from-top-[48%] sm:dsw-rounded-lg",
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="dsw-absolute dsw-right-4 dsw-top-4 dsw-rounded-sm dsw-opacity-70 dsw-ring-offset-background dsw-transition-opacity hover:dsw-opacity-100 focus:dsw-outline-none focus:dsw-ring-2 focus:dsw-ring-ring focus:dsw-ring-offset-2 disabled:dsw-pointer-events-none data-[state=open]:dsw-bg-accent data-[state=open]:dsw-text-muted-foreground">
        <X className="dsw-h-4 dsw-w-4" />
        <span className="dsw-sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogWidgetPortal>
))
DialogWidgetContent.displayName = DialogPrimitive.Content.displayName

const DialogWidgetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "dsw-flex dsw-flex-col dsw-space-y-1.5 dsw-text-center sm:dsw-text-left",
      className
    )}
    {...props}
  />
)
DialogWidgetHeader.displayName = "DialogWidgetHeader"

const DialogWidgetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "dsw-flex dsw-flex-col-reverse sm:dsw-flex-row sm:dsw-justify-end sm:dsw-space-x-2",
      className
    )}
    {...props}
  />
)
DialogWidgetFooter.displayName = "DialogWidgetFooter"

const DialogWidgetTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "dsw-text-lg dsw-font-semibold dsw-leading-none dsw-tracking-tight",
      className
    )}
    {...props}
  />
))
DialogWidgetTitle.displayName = DialogPrimitive.Title.displayName

const DialogWidgetDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("dsw-text-sm dsw-text-muted-foreground", className)}
    {...props}
  />
))
DialogWidgetDescription.displayName = DialogPrimitive.Description.displayName

export {
  DialogWidget,
  DialogWidgetPortal,
  DialogWidgetOverlay,
  DialogWidgetClose,
  DialogWidgetTrigger,
  DialogWidgetContent,
  DialogWidgetHeader,
  DialogWidgetFooter,
  DialogWidgetTitle,
  DialogWidgetDescription,
} 